<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { ref, Ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { debounce } from "vue-debounce";
import { toastApiResult } from "@/toaster";
import SegmentDO = App.Data.Segments.SegmentDO;
import { loadNewSegment } from "@/modules/segments/pages/SegmentUtils";
import SaveSegmentDialog from "@/modules/segments/components/SaveSegmentDialog.vue";
import useDialog from "@/ui/composables/useDialog";
import router from "@/router/router";
import { formatNumber } from "chartjs-graph";
import SegmentSizeDeltaGraph from "@/modules/segments/components/SegmentSizeDeltaGraph.vue";
import dayjs from "dayjs";

const breadcrumbs = [
	{
		title: "Segments",
		disabled: true,
		to: "/segments",
	},
];

const search = ref("");
const itemsPerPage = ref(20);
const showSaveSegmentDialog: Ref<boolean> = ref(false);
const showCohortMonthSelection = ref(false);

const headers = [
	{ title: "Title", key: "title", sortable: true },
	{ title: "Type", key: "type", sortable: true },
	{ title: "Size", key: "computed_size", sortable: true },
	{
		title: "Size Delta (last 52 weeks)",
		key: "measurements",
		width: "35%",
	},
	{ title: "Created At", key: "created_at", sortable: true },
	{ title: "Actions", key: "actions", width: "100", align: "end" },
];

const isLoading = ref(false);
const items = ref([]);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([]);
const dependsOnSegmentId = ref(null);
const dependsOnSegmentDimensionNameClean = ref(null);
const segmentToSave = ref<SegmentDO>(loadNewSegment());
const isEditing = ref(false);

const loadItems = debounce(async ({ page, itemsPerPage, sortBy, search }) => {
	isLoading.value = true;

	const client = useApiClient("segments");

	const response = await client.index({ page, itemsPerPage, sortBy, search });
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const newSegment: Ref<SegmentDO> = ref(loadNewSegment());

const removeSegment = async (segment: SegmentDO) => {
	const confirm = await useDialog().confirm({
		title: "Delete Segment",
		message: `Are you sure you want to delete ${segment.title}?`,
		isDelete: true,
	});

	if (!confirm) {
		return;
	}

	const response = await toastApiResult(
		useApiClient("segments").delete(segment.uuid)
	);
	if (response.isOk()) {
		successCallback();
	}
};

const editSegment = (segment: SegmentDO) => {
	segmentToSave.value = { ...segment };
	showSaveSegmentDialog.value = true;
	isEditing.value = true;
};

const successCallback = (response: SegmentDO | null = null) => {
	loadItems({
		page: page.value,
		itemsPerPage: itemsPerPage.value,
		sortBy: sortBy.value,
	});

	if (response && !isEditing.value) {
		router.push({
			name: "segments.configuration",
			params: { resource_type: "segment", resource_uuid: response.uuid },
		});
	}
};

const createSegmentFromExisting = (
	baseSegment: any,
	segmentDimensionNameClean:
		| "segmentcustomer"
		| "segmentlostcustomer"
		| "segmentreservation"
) => {
	dependsOnSegmentId.value = baseSegment.id;
	dependsOnSegmentDimensionNameClean.value = segmentDimensionNameClean;
	showCohortMonthSelection.value =
		segmentDimensionNameClean === "segmentlostcustomer";

	newSegment.value.type = baseSegment.type;

	if (segmentDimensionNameClean === "segmentlostcustomer") {
		newSegment.value.title = `Lost customers ${baseSegment.title}`;
	} else {
		newSegment.value.title = "";
	}

	showSaveSegmentDialog.value = true;
};

watch(showSaveSegmentDialog, (v) => {
	// reset variables when modal is closed
	if (!v) {
		dependsOnSegmentId.value = null;
		dependsOnSegmentDimensionNameClean.value = null;
		newSegment.value.title = "";
		showCohortMonthSelection.value = false;
	}
});

const addDeltaToMeasurements = (data) => {
	return data.map((item, index) => {
		const previous = data[index - 1];
		const delta = index === 0 ? 0 : item.computed_size - previous.computed_size;

		return {
			...item,
			delta,
		};
	});
};

const typePlurals = {
	customer: "Customers",
	reservation: "Reservations",
};
</script>

<template>
	<save-segment-dialog
		:segment-to-save="isEditing ? segmentToSave : newSegment"
		:depends-on-segment-id="dependsOnSegmentId"
		:depends-on-segment-name-clean="dependsOnSegmentDimensionNameClean"
		:on-success="successCallback"
		:is-editing="isEditing"
		:show-cohort-month-selection="showCohortMonthSelection"
		v-model="showSaveSegmentDialog"
	/>

	<div>
		<main-breadcrumbs :breadcrumbs="breadcrumbs" />
		<v-card>
			<div class="d-flex flex-nowrap pa-4">
				<v-text-field
					v-model="search"
					label="Search"
					class="mr-3"
					prepend-inner-icon="mdi-magnify"
					variant="filled"
					elevation="4"
					hide-details
					single-line
				/>
				<v-btn
					variant="elevated"
					prepend-icon="fa: fa-light fa-plus"
					height="48"
					@click="
						isEditing = false;
						showSaveSegmentDialog = true;
					"
				>
					{{ $t("segments.add_segment") }}
				</v-btn>
			</div>
			<div class="px-4">
				<v-data-table-server
					v-model:items-per-page="itemsPerPage"
					v-model:page="page"
					v-model:sort-by="sortBy"
					:search="search"
					:headers="headers"
					:items="items"
					:items-length="totalItemCount"
					:loading="isLoading"
					@update:options="loadItems"
				>
					<template v-slot:item.title="{ item }">
						<router-link
							class="router-link-primary"
							:to="{
								name: 'segments.configuration',
								params: { resource_type: 'segment', resource_uuid: item.uuid },
							}"
						>
							{{ item.title }}
						</router-link>
					</template>
					<template v-slot:item.type="{ item }">
						<v-chip density="compact" color="on-surface">
							{{ item.has_dependency_on_lost_customer_segment ? "lost-" : ""
							}}{{ item.type }}
						</v-chip>
					</template>
					<template v-slot:item.computed_size="{ item }">
						{{ formatNumber(item.computed_size) }}
					</template>
					<template v-slot:item.measurements="{ item }">
						<SegmentSizeDeltaGraph
							:has-measurements="!item.needs_new_size_measurements"
							:data="addDeltaToMeasurements(item.measurements)"
						/>
					</template>
					<template v-slot:item.created_at="{ item }">
						{{ dayjs(item.created_at).format("MMM D YYYY") }}
					</template>
					<template v-slot:item.actions="{ item }">
						<div class="d-flex w-100 ga-2">
							<v-spacer />
							<v-btn
								size="30"
								@click="editSegment(item)"
								icon
								color="primary"
								variant="text"
							>
								<v-icon size="18">fa: fa-light fa-pencil</v-icon>
							</v-btn>
							<v-btn
								size="30"
								@click="removeSegment(item)"
								icon
								color="error"
								variant="text"
							>
								<v-icon size="18">fa: fa-light fa-trash-can</v-icon>
							</v-btn>
							<v-menu location="bottom right">
								<template v-slot:activator="{ props }">
									<v-btn
										size="30"
										v-bind="props"
										icon="fa: fa-regular fa-ellipsis-vertical"
										variant="text"
										color="on-surface"
									/>
								</template>
								<v-list class="pa-1 mt-2">
									<v-list-subheader inset class="pt-0 mt-0">
										Use as starting point for new segment with
									</v-list-subheader>

									<v-list-item
										class="font-weight-regular"
										@click="
											createSegmentFromExisting(
												item,
												item.type === 'customer'
													? 'segmentcustomer'
													: 'segmentreservation'
											)
										"
									>
										<template v-slot:prepend>
											<v-icon> mdi-account-multiple-plus </v-icon>
										</template>
										{{ typePlurals[item.type] }} that are in this segment
									</v-list-item>
									<v-list-item
										:disabled="
											item.has_dependency_on_lost_customer_segment ||
											item.type !== 'customer'
										"
										class="font-weight-regular"
										@click="
											createSegmentFromExisting(item, 'segmentlostcustomer')
										"
									>
										<template v-slot:prepend>
											<v-icon>mdi-account-multiple-minus</v-icon>
										</template>
										{{ typePlurals[item.type] }} that were lost in this segment
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</template>
				</v-data-table-server>
			</div>
		</v-card>
	</div>
</template>

<style scoped lang="scss"></style>
